import type { CollectionSummaryJSONObject, TagJSONObject } from '@ikegps/core-api';
import { computed, makeObservable } from 'mobx';
import type { PanelCollapseState } from '../../components/collapsible';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { handleMouseMove } from '../../dom';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { TagFetchState, type TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { TagSortState } from '../../state/tag.sort.state';
import { AppStore } from '../../store';
import { CSS as CollectionCardCSS } from '../collection.list/collection.card.style';
import { CollectionList } from '../collection.list/collection.list';
import { CollectionToolbar } from '../collection.toolbar/collection.toolbar';
import { scrollSelectedIntoListView } from '../../util/scroll-into-list-view';
import { TagCard } from './tag.card';
import { CSS, Style } from './tag.list.style';

addStyles(Style);

const CONTENT_KEY = 'tag-list';

export class TagList extends IMithrilStateComponent {
	state: TagState;
	toolbar: CollectionToolbar;

	tagCards: { [id: string]: TagCard } = {};

	constructor(state: TagState, collapse?: PanelCollapseState) {
		super();
		makeObservable(this);

		this.state = state;
		this.toolbar = new CollectionToolbar(this.state, {
			title: T.TAG_TITLE,
			collapse,
		});

		this.reaction(
			() => CurrentState.inspectedCollectionRevision,
			(revision) => {
				if (revision != null) {
					scrollSelectedIntoListView(CSS.cards.container, CollectionCardCSS.isInspected);
				}
			}
		);
	}

	override onRemove() {
		super.onRemove();
		this.tagCards = {};
	}

	/** Taken from the toolbar so the rail and the toolbar can't show different names. */
	get panelTitle(): string {
		return this.toolbar.title;
	}

	get nodes(): MithrilVNode {
		const visibleTags = this.state.visibleTags;

		let content: MithrilVNode;

		if (visibleTags.length !== 0) {
			const cardChildren: MithrilVNode[] = [];
			for (const tag of visibleTags) {
				let tagCard = this.tagCards[tag.objectId];
				if (tagCard == null) {
					tagCard = this.tagCards[tag.objectId] = new TagCard(this.state, tag);
				}

				cardChildren.push(tagCard.view());

				let child: MithrilVNode | null = null;
				if (CurrentState.expandedTagId === tag.objectId) {
					child = this.viewExpandedTag();
				} else if (
					Services.Search.filterState.hasFilter() &&
					Services.Search.collectionSummaries[tag.objectId] != null
				) {
					child = this.viewFilteredCollectionList(tag);
				}

				if (child != null) {
					cardChildren.push(child);
				}
			}

			if (this.state.hasMore) {
				cardChildren.push(this.loadMoreButton());
			}

			content = m('div', { className: CSS.cards.container }, cardChildren);
		} else if (this.state.fetchState === TagFetchState.UNKNOWN) {
			content = this.viewLoadingResult();
		} else if (this.state.fetchState !== TagFetchState.HAS_TAGS) {
			content = this.viewEmptyResult();
		} else {
			content = this.viewSearchEmptyResult();
		}

		content.attrs.key = CONTENT_KEY;

		return m('div', { className: CSS.container, onmousemove: handleMouseMove }, [this.toolbar.view(), content]);
	}

	private loadMoreButton(): MithrilVNode {
		return m(
			'div',
			{ key: 'load-more', className: CSS.loadmore },
			m('button', { className: 'mdl-button', onclick: this.state.loadMore }, Translate(T.LOAD_MORE))
		);
	}

	@computed get expandedCollections(): CollectionSummaryJSONObject[] | null {
		const tagSummary = AppStore.TagSummary.get(CurrentState.expandedTagId);
		if (tagSummary == null) {
			return null;
		}

		const collectionSummaries = [];
		for (const revision of tagSummary.revisions) {
			const collectionSummary = AppStore.CollectionSummary.get(revision);
			if (collectionSummary != null) {
				collectionSummaries.push(collectionSummary);
			}
		}

		if (collectionSummaries.length !== tagSummary.revisions.length) {
			return null;
		}

		TagSortState.sortSummaries(collectionSummaries);
		CurrentState.Tag.resetLastSelectedCollectionIndex();
		CurrentState.Tag.setExpandedCollectionsUUIDs(collectionSummaries.map((_) => _.uuid));
		return collectionSummaries;
	}

	private viewExpandedTag(): MithrilVNode {
		if (CurrentState.expandedTagId == null) {
			return null;
		}

		const summaries = this.expandedCollections;
		if (summaries === null) {
			return CollectionList.viewLoading(CurrentState.expandedTagId);
		}

		const tag = AppStore.Tag.get(CurrentState.expandedTagId);
		return CollectionList.view(this.state, tag, this.expandedCollections);
	}

	private viewFilteredCollectionList(tag: TagJSONObject): MithrilVNode {
		const collectionSummaries = Services.Search.collectionSummaries[tag.objectId];
		return CollectionList.view(this.state, tag, collectionSummaries);
	}

	private viewEmptyResult(): MithrilVNode {
		return m('div', { className: `${CSS.cards.container} ${CSS.cards.isEmpty}` }, [
			m(
				'div',
				{ className: CSS.empty.message, key: `${CONTENT_KEY}-empty-message` },
				Translate(T.TAG_FILTER__SEARCH_EMPTY)
			),
		]);
	}

	private viewLoadingResult(): MithrilVNode {
		return m('div', { className: `${CSS.cards.container} ${CSS.cards.isEmpty}` }, Spinner.view());
	}

	private viewSearchEmptyResult(): MithrilVNode {
		return m('div', { className: `${CSS.cards.container} ${CSS.cards.isEmpty}` }, [
			m(
				'div',
				{ className: CSS.empty.message, key: `${CONTENT_KEY}-empty-message` },
				Translate(T.TAG_FILTER__SEARCH_EMPTY)
			),
			m(
				'button',
				{
					key: `${CONTENT_KEY}-clear-search`,
					className: `mdl-button ${CSS.empty.reset}`,
					onclick: () => Services.Search.reset(),
				},
				Translate(T.CLEAR_SEARCH)
			),
		]);
	}
}
